import { useEffect, useRef, useState } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils'

export interface DateRange { from: string; to: string }

const fmt = (d: Date) => d.toISOString().slice(0, 10)
const fmtDisplay = (v: string) => {
  try { return new Date(`${v}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }
  catch { return v }
}

function startOfWeek(d: Date) {
  const x = new Date(d)
  const day = x.getDay()
  x.setDate(x.getDate() - (day === 0 ? 6 : day - 1))
  return x
}

// Preset ranges recomputed against "today" on every render — a stored "This Month" range
// from yesterday should still mean the current month today, not go stale.
function presetRanges(): Record<string, DateRange> {
  const today = new Date()
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
  const last3 = new Date(today); last3.setDate(last3.getDate() - 2)
  const last7 = new Date(today); last7.setDate(last7.getDate() - 6)
  const startMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
  const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1)
  const startYear = new Date(today.getFullYear(), 0, 1)

  return {
    'Today':       { from: fmt(today),             to: fmt(today) },
    'Yesterday':   { from: fmt(yesterday),          to: fmt(yesterday) },
    'Last 3 Days': { from: fmt(last3),              to: fmt(today) },
    'This Week':   { from: fmt(startOfWeek(today)), to: fmt(today) },
    'Last 7 Days': { from: fmt(last7),              to: fmt(today) },
    'This Month':  { from: fmt(startMonth),         to: fmt(today) },
    'Last Month':  { from: fmt(lastMonthStart),     to: fmt(lastMonthEnd) },
    'This Year':   { from: fmt(startYear),          to: fmt(today) },
  }
}

// Convenience default for callers that want a sensible initial range (current month to date).
export function defaultDateRange(): DateRange {
  return presetRanges()['This Month']
}

export function DateRangePicker({ from, to, onChange, className }: {
  from: string; to: string; onChange: (range: DateRange) => void; className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(from)
  const [draftTo, setDraftTo] = useState(to)
  const ref = useRef<HTMLDivElement>(null)
  const presets = presetRanges()
  const activePreset = Object.entries(presets).find(([, r]) => r.from === from && r.to === to)?.[0]
  const presetLabel = (key: string) => t(`dateRangePicker.presets.${key}`, key)

  useEffect(() => { setDraftFrom(from); setDraftTo(to) }, [from, to])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const label = (activePreset && presetLabel(activePreset)) || (from && to ? `${fmtDisplay(from)} – ${fmtDisplay(to)}` : t('dateRangePicker.selectRange'))

  const applyPreset = (r: DateRange) => { onChange(r); setOpen(false) }
  const applyCustom = () => { if (draftFrom && draftTo) { onChange({ from: draftFrom, to: draftTo }); setOpen(false) } }
  const clear = () => { onChange({ from: '', to: '' }); setOpen(false) }

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:border-brand-300 transition-colors min-w-52"
      >
        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
        <span className="text-gray-700 font-medium truncate">{label}</span>
        <ChevronDown size={13} className={cn('text-gray-400 ms-auto flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-20 top-full start-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg flex overflow-hidden">
          <div className="w-36 py-2 border-e border-gray-100">
            {Object.entries(presets).map(([key, r]) => (
              <button
                key={key}
                onClick={() => applyPreset(r)}
                className={cn(
                  'w-full text-start px-3 py-1.5 text-xs font-medium transition-colors',
                  activePreset === key ? 'text-brand-600 bg-brand-50' : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                {presetLabel(key)}
              </button>
            ))}
          </div>
          <div className="p-3 space-y-2 w-56">
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{t('dateRangePicker.customRange')}</div>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-gray-400">{t('dateRangePicker.from')}</span>
              <input
                type="date" value={draftFrom} max={draftTo || undefined}
                onChange={e => setDraftFrom(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-gray-400">{t('dateRangePicker.to')}</span>
              <input
                type="date" value={draftTo} min={draftFrom || undefined}
                onChange={e => setDraftTo(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={clear} className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors">
                {t('common.clear')}
              </button>
              <button
                onClick={applyCustom}
                disabled={!draftFrom || !draftTo}
                className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-semibold disabled:opacity-50 transition-colors"
              >
                {t('common.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
